import { useCallback, useEffect, useRef, useState } from "react"


export type Position = {
    left: number,
    right: number,
    top: number,
    bottom: number
}

export type Dimensions = {
    width: number,
    height: number
}

export type Delta = {
    x: number,
    y: number
}


export type ResizeableReturnType<C, H> = {
    delta?: Delta,
    originalDimensions?: Dimensions,
    isResizing: boolean,
    containerRef: React.Ref<C>,
    handleRef: React.Ref<H>
}

const useResizeable = <C extends HTMLElement,H extends HTMLElement>(minWidth=0, minHeight = 0): ResizeableReturnType<C, H> => {
    const containerRef = useRef<C>(null);
    const handleRef = useRef<H>(null);
    const [state, setState] = useState<{delta?: Delta, originalDimensions?: Dimensions, isResizing: boolean}>({isResizing: false})
    const containerStartDimensions = useRef<Dimensions | undefined>(undefined)
    const cursorStartPosition = useRef<Position | undefined>(undefined)
    const lastDelta = useRef<Delta | undefined>(undefined);

    const onMouseMove = useCallback((e: MouseEvent) => {
        const cursorStart = cursorStartPosition.current;
        const  containerDimensions = containerStartDimensions.current;
        if (!cursorStart || !containerDimensions) return;
        let deltaX = e.clientX - cursorStart.left;
        let deltaY = e.clientY - cursorStart.top;

        if (containerDimensions.width + deltaX < minWidth) {
            deltaX = minWidth - containerDimensions.width;
        }
        if (containerDimensions.height + deltaY < minHeight) {
            deltaY = minHeight - containerDimensions.height;
        }

        lastDelta.current = {
            x: deltaX,
            y: deltaY
        }
        setState({
            delta: lastDelta.current,
            originalDimensions: containerStartDimensions.current,
            isResizing: true
        })
    }, [])

    const onMouseUp = useCallback(() => {
        setState({
            delta: lastDelta.current,
            originalDimensions: containerStartDimensions.current,
            isResizing: false
        })
        document.removeEventListener("mouseup", onMouseUp)
        document.removeEventListener("mousemove", onMouseMove)
        document.removeEventListener("contextmenu", onMouseUp)
    }, [])
    
    const onMouseDown = useCallback((e: MouseEvent) => {
        const target = e.target as HTMLElement;
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;

        const container = containerRef.current
        if (!container) return

        if (e.stopPropagation) e.stopPropagation()
        if (e.preventDefault) e.preventDefault()

        const bounds = container.getBoundingClientRect()

        containerStartDimensions.current = {
            width: bounds.width,
            height: bounds.height
        };
        cursorStartPosition.current = {
            left: e.clientX,
            right: window.innerWidth - e.clientX,
            top: e.clientY,
            bottom: window.innerHeight - e.clientY

        }
        setState({
            delta: {
                x: 0, y: 0
            },
            originalDimensions: containerStartDimensions.current,
            isResizing: true
        })
        document.addEventListener("mousemove", onMouseMove)
        document.addEventListener("mouseup", onMouseUp)
        document.addEventListener("contextmenu", onMouseUp)
    }, [])

    useEffect(() => {
        const container = containerRef.current;
        const handle = handleRef.current;
        if (container && handle) {
            handle.addEventListener("mousedown", onMouseDown)
        }
        return () => {
            if (container && handle) {
                handle.removeEventListener("mousedown", onMouseDown)
            }
        }
    }, [containerRef, handleRef])

    return {
        ...state,
        containerRef,
        handleRef,
    }
}

export default useResizeable